import { useEffect, useMemo, useState } from "react";
import { get } from "../../../helpers/aws_api_helper";
import { GET_LAMBDA_LIST_OF_ORGS_AND_ACCOUNTS } from "../../../helpers/url_helper";
import getOrgCode from "../../../helpers/jwt-token-access/getOrgCode";
import { shouldFilterUsername } from "../../../helpers/util_helper";

const requests = new Map();

const loadMspUsers = (mspOrgCode) => {
  if (!requests.has(mspOrgCode)) {
    const request = get(GET_LAMBDA_LIST_OF_ORGS_AND_ACCOUNTS, {
      headers: { "X-Cloud-Catcher-Scope": mspOrgCode },
    }).then((res) => {
      if (!res || res.status !== 200) throw new Error("Unable to load MSP users.");
      const users = Array.isArray(res.usersConfig) ? res.usersConfig : [];
      return users.filter((user) => user?.email && !shouldFilterUsername(user.username));
    });
    request.catch(() => requests.delete(mspOrgCode));
    requests.set(mspOrgCode, request);
  }
  return requests.get(mspOrgCode);
};

/**
 * Users of the primary (MSP) organization the scoped sub-org or customer sits under.
 * The scoped org's own users list does not include them, so they are read with the
 * request scoped to the primary org. Not applicable when the primary org itself is scoped.
 */
const useMspUsers = () => {
  const mspOrgCode = localStorage.getItem("mspOrgCode") || "";
  const applicable = Boolean(mspOrgCode) && mspOrgCode !== getOrgCode();
  const [state, setState] = useState({ users: [], loading: applicable, error: null });

  useEffect(() => {
    if (!applicable) {
      setState({ users: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    loadMspUsers(mspOrgCode).then(
      (users) => {
        if (!cancelled) setState({ users, loading: false, error: null });
      },
      (error) => {
        if (!cancelled) setState({ users: [], loading: false, error: error?.message || "Unable to load MSP users." });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [applicable, mspOrgCode]);

  return useMemo(() => ({ applicable, ...state }), [applicable, state]);
};

export default useMspUsers;
